/* eslint-disable */
import Vue from 'vue'
import { createVideoProcessor, VideoProcessor } from '@/plugins/ocular-video-processor'
import type { SupportInfo, ProcessorStats } from '@/plugins/ocular-video-processor'

/**
 * Bridge between the `@ocular/video-processor` plugin and the Vue
 * application. Manages a singleton {@link VideoProcessor} instance and
 * exposes it on `Vue.prototype.$videoProcessor` so every component can
 * access it without prop drilling.
 *
 * This file is **project-specific** and is NOT part of the reusable
 * plugin. When the plugin is extracted to its own npm package, this
 * bridge stays in the consuming application.
 */

let instance: VideoProcessor | null = null

/**
 * Get (or lazily create) the singleton {@link VideoProcessor}.
 *
 * The processor is created with the OpenTok adapter by default. It is
 * not yet attached to a publisher — call `processor.attach(publisher)`
 * after the publisher stream is created.
 */
export function getVideoProcessor (): VideoProcessor {
  if (!instance) {
    instance = createVideoProcessor({ adapter: 'opentok' })
  }
  return instance
}

/**
 * Destroy the singleton processor and release all resources.
 * After this call, the next {@link getVideoProcessor} will create a
 * fresh instance.
 */
export async function destroyVideoProcessor (): Promise<void> {
  if (instance) {
    await instance.destroy()
    instance = null
  }
}

/**
 * Check whether a processor instance currently exists (regardless of
 * whether it is attached to a publisher).
 */
export function hasVideoProcessor (): boolean {
  return instance !== null
}

/**
 * Convenience wrapper to query browser support without needing to
 * instantiate the full processor. Useful in components that need to
 * show/hide UI controls based on capabilities.
 */
export function getVideoProcessorSupport (): SupportInfo {
  return getVideoProcessor().isSupported()
}

/**
 * Convenience wrapper to get a performance stats snapshot.
 */
export function getVideoProcessorStats (): ProcessorStats {
  if (!instance) {
    return {
      fps: 0,
      droppedFrames: 0,
      totalFramesProcessed: 0,
      nsfwAnalysisCount: 0,
      avgNsfwLatencyMs: 0,
      avgFrameProcessingMs: 0,
      uptime: 0,
      activeFiltersCount: 0
    }
  }
  return instance.getStats()
}

Vue.prototype.$videoProcessor = {
  get: getVideoProcessor,
  destroy: destroyVideoProcessor,
  has: hasVideoProcessor,
  support: getVideoProcessorSupport,
  stats: getVideoProcessorStats
}
